import React, {useEffect, useMemo, useRef, useState} from 'react';
import makeStyles from "@material-ui/core/styles/makeStyles";
import {fade} from "@material-ui/core/styles/colorManipulator";
import {getTextDimensions} from "./stringutils";
import useTheme from "@material-ui/core/styles/useTheme";
import {useTranslation} from "react-i18next";
import emojiRegex from "emoji-regex";

const useStyles = makeStyles(theme => ({
    renderPart: {
        position: 'absolute',
        boxSizing: 'border-box'
    },
    selectText: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        color: theme.palette.getContrastText(theme.palette.primary.light)
    },
    selectTextNum: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        color: theme.palette.getContrastText(theme.palette.primary.light),
        backgroundColor: fade(theme.palette.primary.light, 0.27),
        fontWeight: 'bold',
        textAlign: 'center'
    }
}))

const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

const emojiReplaceRegexp = emojiRegex();

export default React.memo( (props) => {
    if (props.activeTextInputRef) {
        return <RenderEditText {...props} />
    }
    return <RenderText {...props} />
});

const RenderEditText = ({
                            renderPart,
                            resource,
                            activeTextInputRef,
                            handleTextInputBlur,
                            handleTextInputFocus,
                            handleTextInputChange,
                            scaleRatio,
                            zoomResourceId
                        }) => {
    console.log('Render string', renderPart);
    const classes = useStyles ();
    const theme = useTheme();

    const scaledRenderPart = useMemo(() => {
        return {
            width: renderPart.size.width * scaleRatio,
            height: renderPart.size.height * scaleRatio,
            x: renderPart.position.x * scaleRatio,
            y: renderPart.position.y * scaleRatio,
        }
    }, [renderPart, scaleRatio])

    useEffect( () => {
        console.log('Edit text load')
        handleChange()
    }, [resource])

    const horizontalAlign = resource?.font.overrideHorizontalAlign || renderPart.font.horizontalAlign;
    const verticalAlign = resource?.font.overrideVerticalAlign || renderPart.font.verticalAlign;
    const fontFamily = resource?.font.overrideFamily || renderPart.font.family;
    const color = resource?.font.overrideColor || renderPart.font.color;
    const fontSize = resource?.font.overrideSize || renderPart.font.size;


    const handleChange = (e) => {
        // Replace all emojis (emojiReplace has /g operator)
        let text = (e?.target?.value ?? (resource?.font.text || ''))
            .replace(emojiReplaceRegexp, "");
        console.log('handleChange', e?.target?.value, text);
        const dim = getTextDimensions(fontFamily, text, fontSize, 'pt', renderPart.size.width);
        let tooLarge = false;

        if (dim.width > renderPart.size.width || dim.height > renderPart.size.height) {
            console.log('Too large', dim.width, renderPart.size.width , dim.height, renderPart.size.height)
            tooLarge = true;
        }

        if (e) {
            if (!tooLarge) {
                handleTextInputChange(text)
            }
            else {
                e.preventDefault();
                console.log('Text is too long!');
            }
        }

        if (!tooLarge) {
            activeTextInputRef.current.style.height = dim.height + 'px'
        }

        //setToLarge(tooLarge);
    }

    let align = 'flex-start';
    switch (horizontalAlign) {
        case "center":
            align = 'center';
            break;
        case "right":
            align = 'flex-end';
            break;
    }

    let valign = 'flex-start';
    switch (verticalAlign) {
        case "center":
            valign = 'center';
            break;
        case "bottom":
            valign = 'flex-end';
            break;
    }

    console.log('Vertical align', valign, verticalAlign)

    let rotate = 0;
    let rotateOriginX = 0;
    let rotateOriginY = 0;

    for (const transformation of renderPart.transformations) {
        if (transformation.type == 'ROTATE') {
            rotate = transformation.rotate;
            rotateOriginY = transformation.originY;
            rotateOriginX = transformation.originX;
        }
    }

    console.log('Render string__', renderPart, rotate, 'the end');

    const styles = {
        fontFamily: fontFamily,
        fontSize: fontSize + 'pt',
        color: color,
        textAlign: horizontalAlign,
        resize: 'none',
        padding: 0,
        border: 'none',
        width: '100%',
        overflowWrap: 'normal',
        overflowX: 'hidden',
        overflowY: 'hidden',
        backgroundColor: 'transparent',
        outline: 'none',
    }

    if (renderPart.font.shadow) {
        const {offsetX, offsetY, opacity, blurRadius, color} = renderPart.font.shadow;
        styles.textShadow = `${offsetX}px ${offsetY}px ${blurRadius}px ${fade(color, parseInt(opacity) / 100)}`
    }

    const outerStyle = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: renderPart.size.width + 'px',
        height: renderPart.size.height + 'px',
        display: 'flex',
        justifyContent: align,
        alignItems: valign,
        boxSizing: 'border-box',
        transform: `scale(${scaleRatio})`,
        transformOrigin: '0 0 0',
    }

    const renderElement = {
        position: 'absolute',
        left: scaledRenderPart.x + 'px',
        top: scaledRenderPart.y + 'px',
        width: scaledRenderPart.width  + 'px',
        height: scaledRenderPart.height + 'px',
        transformOrigin: `${rotateOriginX}px ${rotateOriginY}px`,
        zIndex: 1000
    }

    const overlayStyle = {
        position: 'absolute',
        left: scaledRenderPart.x  + 'px',
        top: scaledRenderPart.y + 'px',
        width: scaledRenderPart.width  + 'px',
        height: scaledRenderPart.height + 'px',
        border: '1px solid ' + theme.palette.primary.main,
        transformOrigin: `${rotateOriginX}px ${rotateOriginY}px`,
    }

    if (rotate) {
        renderElement.transform = `rotate(-${rotate}deg)`
        overlayStyle.transform = `rotate(-${rotate}deg)`
    }


    // If we "submit" the form (Remove keyboard), blur it. (Mobile)
    const handleSubmit = (e) => {
        e.preventDefault();
        // Let's do a blur hack:
     //   setDisabled(true);
        activeTextInputRef.current.blur();
    }

    return (<form onSubmit={handleSubmit} method='POST'>
        {/* Border as own overlay (Underlay) */}
        <div style={overlayStyle} />
        <div style={renderElement}>
            <div style={outerStyle} onClick={() => activeTextInputRef.current.focus()}>
            <textarea name='thetext' style={styles} value={resource?.font.text || ''} ref={activeTextInputRef}
                      onBlur={handleTextInputBlur}
                      onFocus={handleTextInputFocus}
                      onChange={handleChange}/>
            </div>
        </div>

    </form>)

}

const RenderText = ({renderPart, resource, monthStrings, year, handleUserStringClick, scaleRatio, mode, resourceNumber}) => {
    console.log('Render string', renderPart);
    const classes = useStyles ();
    const theme = useTheme();
    const {t} = useTranslation();

    const scaledRenderPart = useMemo(() => {
        return {
            width: renderPart.size.width * scaleRatio,
            height: renderPart.size.height * scaleRatio,
            x: renderPart.position.x * scaleRatio,
            y: renderPart.position.y * scaleRatio,
        }
    }, [renderPart, scaleRatio])


    const horizontalAlign = resource?.font.overrideHorizontalAlign || renderPart.font.horizontalAlign;
    const verticalAlign = resource?.font.overrideVerticalAlign || renderPart.font.verticalAlign;
    const fontFamily = resource?.font.overrideFamily || renderPart.font.family;
    const color = resource?.font.overrideColor || renderPart.font.color;
    const fontSize = resource?.font.overrideSize || renderPart.font.size;

    let textAlign = 'left';
    switch (horizontalAlign) {
        case 'center':
            textAlign = 'center';
            break;
        case "right":
            textAlign = 'right'
            break;
    }
    let valignItems = 'flex-start';
    switch (verticalAlign) {
        case 'center':
        case 'middle':
            valignItems = 'center';
            break;
        case "bottom":
            valignItems = 'flex-end';
            break;
    }

    let rotate = 0;
    let rotateOriginX = 0;
    let rotateOriginY = 0;

    for (const transformation of renderPart.transformations) {
        if (transformation.type == 'ROTATE') {
            rotate = transformation.rotate;
            rotateOriginY = transformation.originY;
            rotateOriginX = transformation.originX;
        }
    }

    console.log('Render string__', renderPart, rotate, 'the end');

    const renderElement = {
        position: 'absolute',
        left: scaledRenderPart.x + 'px',
        top: scaledRenderPart.y + 'px',
        width: scaledRenderPart.width + 'px',
        height: scaledRenderPart.height + 'px',
        transformOrigin: `${rotateOriginX}px ${rotateOriginY}px`,
    }

    const outerStyle = {
        position: 'absolute',
        width: renderPart.size.width + 'px',
        height: renderPart.size.height + 'px',
        top: 0,
        left: 0,
        fontFamily: fontFamily,
        fontSize: fontSize + 'pt',
        color: color,
        display: 'flex',
        alignItems: valignItems,
        textAlign: textAlign,
        lineHeight: 'normal',
        transform: `scale(${scaleRatio})`,
        transformOrigin: '0 0 0',
    }

    if (renderPart.font.shadow) {
        const {offsetX, offsetY, opacity, blurRadius, color} = renderPart.font.shadow;
        outerStyle.textShadow = `${offsetX}px ${offsetY}px ${blurRadius}px ${fade(color, parseInt(opacity) / 100)}`
    }

    if (rotate) {
        renderElement.transform = `rotate(-${rotate}deg)`
    }

    const innerStyles = {
        width: '100%',
        whiteSpace: 'pre',
    }

    let text = "";
    let selectText = "";
    let re;
    if (renderPart.resourceId == 'YEAR_STRING') {
        text = year;
    }
    else if ((re = renderPart.resourceId.match(/MONTH\(([^\(]+)\)_YEAR_STRING/))) {
        let monthNumber = 1;
        if (monthStrings[re[1]]) {
            monthNumber = monthStrings[re[1]].monthNumber;
        }
        text = t(monthNames[monthNumber-1]) + " " + year;
    }
    else if (resource?.userSupplied) {
        text = resource.font?.lines?.join("\n") || '';
        if (!text && handleUserStringClick) {
            outerStyle.backgroundColor = theme.palette.primary.light;
            if (mode != 'select-user-text') {
                selectText = t('Select text');
            }
        }
    }
    else {
        console.log('ERROR LOADING RESOURCE', renderPart, resource);
    }

    return <div style={renderElement} className="render-element">
        <div className={classes.renderPart} style={outerStyle}>
            <div style={innerStyles}>
                {text}
            </div>
        </div>
        {selectText && <div className={classes.selectText}>{selectText}</div>}
        {mode == 'select-user-text' && <div className={classes.selectTextNum}>{resourceNumber}</div>}
    </div>
}